'use client'

import { BulletPoint } from '@repo/ui/BulletPoint'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { COISPartyCard } from '@repo/ui/COISPartyCard'
import { confirm } from '@repo/ui/Confirm'
import { LargeUserIcon } from '@repo/ui/LargeUserIcon'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { useCOISStore } from '@repo/store/useCOISStore'
import { useNotificationStore } from '@repo/store/useNotificationStore'
import { useState } from 'react'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const COISIntroduction = ({ wideMode, handlePrevNext }) => {
  const { addParty, selectParty, partyIds, parties, updateCOISWizardSidebar, removeParty, reset } = useCOISStore()
  const { coisSelect } = useNotificationStore()
  const [tipsShowing, setTipsShowing] = useState(partyIds.length === 0)
  const [partiesToShow, setPartiesToShow] = useState(partyIds ? [...partyIds] : null)

  const startNewSearch = () => {
    const partyId = Date.now()
    addParty({ partyId })
    loadPartyDetails(partyId)
  }

  const loadPartyDetails = (partyId, targetPageIncrement = 1) => {
    selectParty({ partyId })
    updateCOISWizardSidebar()

    // if they haven't set up the party yet, take them to the party details screen
    if (!parties[partyId].displayName) {
      targetPageIncrement = 1
    }
    handlePrevNext(targetPageIncrement)
  }

  const handleClickBack = () => {
    updateCOISWizardSidebar()

    window.location.href.includes(
      '?wizard=COIS-NewMatter-NewClient-NotificationApproval&stage=conflict&step=introduction'
    ) || window.location.href.includes('?wizard=COIS-NewMatter-NotificationApproval&stage=conflict&step=introduction')
      ? handlePrevNext(-2)
      : handlePrevNext(-1)
  }

  const deleteParty = async (e, partyId) => {
    e.stopPropagation()
    const shouldReset = await confirm('Are you sure you want to delete this party?')
    if (shouldReset) {
      removeParty({ partyId })
      setPartiesToShow(partyIds ? [...partyIds] : null)
      setTipsShowing(parties.length === 0)
      // debugger
      updateCOISWizardSidebar()
    }
  }

  const resetCOISstore = async () => {
    const shouldReset = await confirm('Are you sure you want to reset the search?')
    if (shouldReset) {
      reset()
      setPartiesToShow(null)
      setTipsShowing(true)
      updateCOISWizardSidebar()
    }
  }

  return (
    <>
      <WizardPanel data-testid="COISIntroduction">
        <MUIStack direction="row" alignItems="center" justifyContent="space-between">
          <WizardIconTitle title="Introduction" icon={LargeUserIcon} />

          {partiesToShow?.length ? (
            <MUIButton
              variant="text"
              onClick={() => setTipsShowing(!tipsShowing)}
              sx={{ mb: 0, textDecoration: 'underline', textTransform: 'none', color: 'primary.main' }}
            >
              Tips
              <ChevronRightIcon
                sx={{ transition: 'transform 250ms', transform: `rotate(${tipsShowing ? -90 : 90}deg)` }}
              />
            </MUIButton>
          ) : null}
        </MUIStack>

        <MUIStack sx={{ transition: 'all 250ms', height: tipsShowing ? '136px' : 0, overflow: 'hidden' }}>
          <MUITypography variant="body1" sx={{ mt: '16px', mb: '24px' }}>
            This wizard will take you through the steps of the Conflict of interest search.
          </MUITypography>

          <MUIStack direction="column" gap={2}>
            <BulletPoint text="Search a single party at a time" />
            <BulletPoint text="If you wish to complete a search for another party, you can start the search again" />
            <BulletPoint text="All previous searches will be saved and available to review below" />
          </MUIStack>
        </MUIStack>
      </WizardPanel>

      {partiesToShow?.length ? (
        <MUIStack direction="column" gap={4} sx={{ mt: 16 }}>
          <MUIStack direction="row" justifyContent="space-between" alignItems="center">
            <MUITypography variant="h6" sx={{ mb: 0 }}>
              Previous searches
            </MUITypography>

            <TextLinkButton
              title="Clear search history"
              sx={{ color: 'primary.main', width: 'auto', padding: 0 }}
              onClick={resetCOISstore}
            />
          </MUIStack>

          {partiesToShow.map((partyId) => (
            <COISPartyCard
              key={partyId}
              partyId={partyId}
              parties={parties}
              editParty={() => loadPartyDetails(partyId, 1)}
              deleteParty={deleteParty}
            />
          ))}
        </MUIStack>
      ) : null}

      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={startNewSearch}
        handleClickBack={handleClickBack}
        continueButtonText="Search new party"
        backButtonVisible={coisSelect === true ? true : false}
      />
    </>
  )
}

COISIntroduction.propTypes = {
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func
}
